import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Ban, Clock, ListPlus, XCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { postRecoveryApi, type CommentFlag, type TaskComment } from './api'
import { formatDateTime, formatDate } from '@/lib/dateUtils'
import { useCan } from '@/hooks/useCan'

/**
 * What an engineer noticed about one step, written down while they are still standing in front
 * of it.
 *
 * THE FLAG IS THE RECORD. THE WORDS ARE OPTIONAL. Nobody composes prose in the middle of a
 * recovery, and a form that insists on it collects nothing at all — so Save goes live the instant
 * a flag is picked, and the comment box says out loud what it is for rather than merely lacking a
 * required marker. There is no "Note" option: a note is what you have when nothing is wrong, so
 * typing words and ticking nothing IS the note, and offering it as a fifth kind of fault asked a
 * question with one possible answer.
 *
 * That is not a nicety. Three task durations are measured in the whole database; for almost every
 * step the only evidence the runbook is wrong is somebody saying so here, in the ninety seconds
 * they have spare. A flag that goes unrecorded is not a gap in a chart — it is the same step being
 * wrong again on the next recovery.
 *
 * No tally of flags is drawn across the list below. At these counts a "2 × timing is wrong" chip
 * reads as a finding, and two observations cannot carry one.
 */

const FLAGS: {
  value: CommentFlag
  labelKey: string
  label: string
  hintKey: string
  hint: string
  icon: typeof XCircle
  /** Pastel tint, applied only to the chosen option and to that flag's chip in the list below. */
  tint: string
  text: string
}[] = [
  {
    value: 'wrong_step',
    labelKey: 'postRecovery.commentFlagWrongStep', label: 'The step is wrong',
    hintKey: 'postRecovery.commentFlagWrongStepHint', hint: 'The step is wrong or does not work',
    icon: XCircle,
    tint: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-700 dark:text-rose-300',
  },
  {
    value: 'wrong_timing',
    labelKey: 'postRecovery.commentFlagWrongTiming', label: 'The timing is wrong',
    hintKey: 'postRecovery.commentFlagWrongTimingHint', hint: 'The time allowed is wrong',
    icon: Clock,
    tint: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300',
  },
  {
    value: 'missing_step',
    labelKey: 'postRecovery.commentFlagMissingStep', label: 'Something is missing',
    hintKey: 'postRecovery.commentFlagMissingStepHint', hint: 'Something is missing before or after this',
    icon: ListPlus,
    tint: 'bg-violet-50 dark:bg-violet-950/40', text: 'text-violet-700 dark:text-violet-300',
  },
  {
    value: 'unnecessary_step',
    labelKey: 'postRecovery.commentFlagUnnecessaryStep', label: 'The step was not needed',
    hintKey: 'postRecovery.commentFlagUnnecessaryStepHint', hint: 'This step was not needed',
    icon: Ban,
    tint: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300',
  },
  // 'note' is deliberately NOT offered. It is not a kind of thing that is wrong with a step, it is
  // what you have when nothing is: an engineer who types words and ticks nothing has left a note,
  // and asking them to also classify it as one is a question with only one possible answer. The
  // value still exists on the server and is what a text-only submission is stored as.
]

const FLAG_BY_VALUE = new Map(FLAGS.map((f) => [f.value, f]))

/** The shape axios throws on a rejected save; the sibling recovery dialogs read it the same way. */
function errorMessage(e: unknown): string | null {
  const withResponse = e as { response?: { data?: { message?: string } } } | null
  return withResponse?.response?.data?.message ?? (e as Error | null)?.message ?? null
}

export function TaskCommentDialog({
  taskId,
  taskNo,
  taskText,
  open,
  onOpenChange,
}: {
  taskId: number | null
  taskNo?: number | null
  taskText?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  /**
   * MULTIPLE, because a step is often wrong in more than one way at once — the instruction is
   * out of date AND the five minutes it allows was never realistic. Forcing one choice made the
   * engineer decide which complaint to throw away.
   *
   * Each selection is saved as its own row, so the history counts "wrong timing" and "wrong step"
   * separately: they are two different corrections to make to the runbook, not one.
   */
  const [flags, setFlags] = useState<CommentFlag[]>([])
  const toggleFlag = (v: CommentFlag) =>
    setFlags((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  const [text, setText] = useState('')

  const comments = useQuery({
    queryKey: ['post-recovery-task-comments', taskId],
    queryFn: () => postRecoveryApi.taskComments(taskId as number),
    enabled: open && taskId != null,
  })

  const save = useMutation({
    mutationFn: (bodies: { flag: CommentFlag; comment?: string }[]) =>
      // Sequential, not Promise.all: they land in the order the engineer chose them, and a
      // partial failure leaves the earlier flags saved rather than all of them in doubt.
      bodies.reduce(
        (chain, body) => chain.then(() => postRecoveryApi.addTaskComment(taskId as number, body)),
        Promise.resolve() as Promise<unknown>,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post-recovery-task-comments', taskId] })
      // Prefix match on purpose. Every runbook screen keys under this root by requestId, and each
      // of them shows the per-task commentCount that has just changed.
      void queryClient.invalidateQueries({ queryKey: ['recovery-runbook'] })
      // Cleared, but the dialog STAYS OPEN: the flag appearing at the top of the list is the only
      // confirmation there is, and a dialog that vanishes on save leaves people wondering whether
      // it went anywhere and flagging it a second time.
      setFlags([])
      setText('')
    },
  })

  // A flag left selected from the previous task is a flag filed against the wrong step. Reset on
  // every open and on every change of task, not just on close.
  useEffect(() => {
    setFlags([])
    setText('')
    save.reset()
    // save.reset is stable; listing it here would re-clear the form on each mutation state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, open])

  const rows = useMemo(() => {
    const list: TaskComment[] = comments.data ?? []
    // Newest first, and by id when two land in the same second — the server's order is not a
    // contract this screen should depend on.
    return [...list].sort(
      (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0) || b.id - a.id,
    )
  }, [comments.data])

  /** Relative for anything recent, an actual date once "N days ago" stops meaning much. */
  const when = (iso: string) => {
    const ms = Date.parse(iso)
    // An unparseable timestamp is shown as-is rather than swallowed: it is information about the
    // data, and "—" would hide it.
    if (Number.isNaN(ms)) return iso
    const mins = Math.round((Date.now() - ms) / 60_000)
    if (mins < 1) return t('postRecovery.commentJustNow', 'just now')
    if (mins < 60) return t('postRecovery.commentMinutesAgo', '{{n}}m ago', { n: mins })
    const hours = Math.round(mins / 60)
    if (hours < 24) return t('postRecovery.commentHoursAgo', '{{n}}h ago', { n: hours })
    const days = Math.round(hours / 24)
    if (days <= 7) return t('postRecovery.commentDaysAgo', '{{n}}d ago', { n: days })
    return formatDate(ms)
  }

  // Either is enough. Words with nothing ticked is a note; a tick with no words is a complete
  // record on its own. Only an empty form is nothing.
  const canSave = (flags.length > 0 || text.trim().length > 0) && taskId != null && !save.isPending
  // Post-recovery comments are the engineer's: recovery.execute (no assignment check - the run is over).
  const canFlag = useCan('recovery.execute')

  const submit = () => {
    const comment = text.trim()
    if (taskId == null || (flags.length === 0 && !comment)) return
    if (flags.length === 0) {
      save.mutate([{ flag: 'note', comment }])
      return
    }
    // One row per flag. The words, if any, go on every one of them, so a comment written about
    // "wrong step and wrong timing" is readable from either side of the history.
    save.mutate(flags.map((flag) => (comment ? { flag, comment } : { flag })))
  }

  // isError, not a truthy message: a rejection that carries no message must still be visible, or a
  // flag silently fails to save and the engineer walks away believing it was recorded.
  const failure = save.isError
    ? (errorMessage(save.error)
       ?? t('postRecovery.commentSaveFailedUnknown', 'the server did not say why'))
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {taskNo != null
              ? t('postRecovery.commentTitleNumbered', 'Flag step {{n}}', { n: taskNo })
              : t('postRecovery.commentTitle', 'Flag this step')}
          </DialogTitle>
          {/* The step being flagged is QUOTED MATERIAL, not the dialog talking. It read in the
              same muted grey as the section headings below it, so the runbook's own words and
              this screen's furniture were indistinguishable. Set in the foreground colour behind
              a rule, it reads as the thing under discussion. */}
          <DialogDescription asChild>
            <div className="mt-1 border-l-2 border-border pl-3 text-sm leading-relaxed text-foreground">
              {taskText
                ? taskText
                : <span className="italic text-muted-foreground">
                    {t('postRecovery.commentNoTaskText', 'This step has no description recorded.')}
                  </span>}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pt-1">
          {/* Flagging a step is recovery.execute; what is already flagged is shown to everyone. */}
          {canFlag && (
            <>
              <div>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('postRecovery.commentFlagLegend', 'What is wrong with this step?')}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {t('postRecovery.commentFlagOptional', 'tick any that apply')}
                  </span>
                </div>

                {/* Real options with real sentences, not enum values in a <select>. The row keeps the
                    same padding and border whether or not it is chosen, so picking one does not shunt
                    the rest of the dialog down the screen. */}
                <div
                  role="group"
                  className="flex flex-col gap-1.5"
                  aria-label={t('postRecovery.commentFlagLegend', 'What is wrong with this step?')}
                >
                  {FLAGS.map((f) => {
                    const Icon = f.icon
                    const selected = flags.includes(f.value)
                    return (
                      <Label
                        key={f.value}
                        htmlFor={`task-flag-${f.value}`}
                        className={cn(
                          'flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors',
                          selected ? cn(f.tint, 'border-transparent shadow-sm') : 'hover:bg-muted/50',
                        )}
                      >
                        <Checkbox
                          id={`task-flag-${f.value}`}
                          checked={selected}
                          onCheckedChange={() => toggleFlag(f.value)}
                          className="mt-0.5 shrink-0"
                        />
                        <Icon
                          className={cn('mt-px h-4 w-4 shrink-0', selected ? f.text : 'text-muted-foreground')}
                          aria-hidden
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium leading-tight">
                            {t(f.labelKey, f.label)}
                          </span>
                          <span className="mt-0.5 block text-xs font-normal leading-snug text-muted-foreground">
                            {t(f.hintKey, f.hint)}
                          </span>
                        </span>
                      </Label>
                    )
                  })}
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('postRecovery.commentWordsLabel', 'Anything to add?')}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {flags.length > 0
                      ? t('postRecovery.commentWordsOptional', 'optional — the ticks on their own are a complete record')
                      : t('postRecovery.commentWordsIsNote', 'words on their own are saved as a note')}
                  </span>
                </div>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  // Ctrl/Cmd+Enter, because this is filled in one-handed between two other things.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSave) {
                      e.preventDefault()
                      submit()
                    }
                  }}
                  placeholder={t('postRecovery.commentPlaceholder', 'A few words, if you have them')}
                  className="min-h-[64px] text-sm"
                />
              </div>

              {failure && (
                // Inline rather than a toast: a toast fired mid-recovery is missed, and this needs to
                // stay put next to the button that failed.
                <p className="text-xs text-rose-700 dark:text-rose-300">
                  {t('postRecovery.commentSaveFailed', 'Not saved: {{message}}', { message: failure })}
                </p>
              )}
            </>
          )}

          <div className="border-t pt-3">
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('postRecovery.commentExisting', 'Already flagged on this step')}
              </span>
              {rows.length > 0 && (
                <span className="text-[11px] tabular-nums text-muted-foreground">{rows.length}</span>
              )}
            </div>

            {comments.isLoading ? (
              <p className="py-2 text-xs text-muted-foreground">{t('common.loading', 'Loading…')}</p>
            ) : rows.length === 0 ? (
              // Not "No data". What is missing, and what would produce it.
              <p className="max-w-prose py-2 text-xs leading-relaxed text-muted-foreground">
                {t('postRecovery.commentEmpty',
                  'Nothing flagged on this step yet. A flag is recorded the moment someone picks one of the options above, and it stays with the step for whoever runs it next — for most steps it is the only evidence the runbook is wrong, because the clock is rarely running.')}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {rows.map((c) => {
                  const f = FLAG_BY_VALUE.get(c.flag)
                  return (
                    <li key={c.id} className="rounded-lg border p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[11px] font-medium',
                            f ? cn(f.tint, f.text) : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {/* An unrecognised flag prints its stored value rather than nothing —
                              a new flag added server-side must not render as a blank chip. */}
                          {f ? t(f.labelKey, f.label) : c.flag}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {c.userName ?? t('postRecovery.commentUnknownAuthor', 'Unknown engineer')}
                        </span>
                        <span
                          className="text-[11px] text-muted-foreground"
                          title={Number.isNaN(Date.parse(c.createdAt))
                            ? undefined
                            : formatDateTime(c.createdAt)}
                        >
                          {when(c.createdAt)}
                        </span>
                      </div>
                      {c.comment && (
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-snug">
                          {c.comment}
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close', 'Close')}
          </Button>
          {canFlag && (
            <Button type="button" onClick={submit} disabled={!canSave}>
              {save.isPending
                ? t('common.saving', 'Saving…')
                : t('postRecovery.commentSubmit', 'Flag it')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
